import React from 'react';
import { cn } from '../../lib/utils';

// Base Card component for consistent styling
interface BaseCardProps {
  variant?: 'default' | 'bordered' | 'elevated' | 'outline';
  noPadding?: boolean;
}

interface NonClickableCardProps
  extends BaseCardProps,
    Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  children: React.ReactNode;
  clickable?: false;
}

interface ClickableCardProps
  extends BaseCardProps,
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  children: React.ReactNode;
  clickable: true;
}

type CardProps = NonClickableCardProps | ClickableCardProps;

export function Card({
  children,
  variant = 'default',
  noPadding = false,
  clickable = false,
  className,
  ...props
}: CardProps) {
  const variants = {
    default: 'bg-card rounded-lg shadow-card',
    bordered: 'bg-card rounded-lg border border-border',
    elevated: 'bg-card rounded-lg shadow-card-hover',
    outline: 'bg-card rounded-lg shadow-sm border border-border',
  };

  const baseClasses = cn(
    variants[variant],
    !noPadding && 'p-6 sm:p-6', // Maintain same padding, but ensure it's touch-friendly
    clickable &&
      'cursor-pointer hover:shadow-card-hover transition-shadow duration-200 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 min-h-[44px]', // Add focus and hover states for clickable cards
    className
  );

  // If clickable, make it a button element for better accessibility
  if (clickable) {
    return (
      <button className={baseClasses} {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}>
        {children}
      </button>
    );
  }

  return (
    <div className={baseClasses} {...(props as React.HTMLAttributes<HTMLDivElement>)}>
      {children}
    </div>
  );
}

// Card Header component
interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export function CardHeader({ children, className, ...props }: CardHeaderProps) {
  return (
    <div className={cn('mb-4', className)} {...props}>
      {children}
    </div>
  );
}

// Card Body component
interface CardBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export function CardBody({ children, className, ...props }: CardBodyProps) {
  return (
    <div className={cn('space-y-2', className)} {...props}>
      {children}
    </div>
  );
}

// Card Footer component
interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export function CardFooter({ children, className, ...props }: CardFooterProps) {
  return (
    <div className={cn('mt-4 pt-4 border-t border-border', className)} {...props}>
      {children}
    </div>
  );
}

// Card Metadata Row component for consistent metadata display
interface CardMetadataRowProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export function CardMetadataRow({ children, className, ...props }: CardMetadataRowProps) {
  return (
    <div className={cn('flex flex-wrap gap-4', className)} {...props}>
      {children}
    </div>
  );
}
